import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import React from 'react';
import { Select as MantineSelect } from '@mantine/core';
import { Select, TextInput } from 'react-hook-form-mantine';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { Controller, FormProvider } from 'react-hook-form';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useRegistrationDetailsSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/useRegistrationDetailsSection.ts';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { REGISTRATION_DETAILS_FORM_FIELDS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/types.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';
import { BasicDetailsHistoryLink } from '../../components/BasicDetailsHistoryLink/BasicDetailsHistoryLink';

const JurisdictionOption = makeOptionWithTestId(
	TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_JURISDICTION_OPTION
);
const JurisdictionLanguageOption = makeOptionWithTestId(
	TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_JURISDICTION_LANGUAGE_OPTION
);
const LegalFormOption = makeOptionWithTestId(
	TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_LEGAL_FORM_OPTION
);

interface Props {
	hasEditorPermission: boolean;
}

export const RegistrationDetailsSection = ({ hasEditorPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const [editMode, setEditMode] = React.useState(false);

	const {
		registrationDetailsForm,
		onFormSubmit,
		showJurisdictionLanguageSelect,
		jurisdictionLanguages,
		legalFormOptions,
		jurisdictionsOptions,
		jurisdictionNameByValue,
		jurisdictionValueByName,
		isLoading,
		selectedEntityData,
	} = useRegistrationDetailsSection();

	const resetJurisdictionFields = () => {
		registrationDetailsForm.setValue(
			REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_language,
			''
		);
		registrationDetailsForm.setValue(REGISTRATION_DETAILS_FORM_FIELDS.legal_form_id, '');
	};

	const resetLegalForm = () => {
		registrationDetailsForm.setValue(REGISTRATION_DETAILS_FORM_FIELDS.legal_form_id, '');
	};

	return (
		<EditableInfoContainer
			title={tCapManage('basic_details.legal_person_form.title')}
			isEdit={editMode}
			setIsEdit={setEditMode}
			disableButtons={isLoading}
			onUpdate={registrationDetailsForm.handleSubmit(onFormSubmit)}
			onCancel={() => setEditMode(false)}
			hideRemove={true}
			hasEditorPermission={hasEditorPermission}
			skipTopBorder={true}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_EDIT_BUTTON,
				'REGISTRATION_DETAILS'
			)}
			updateBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_UPDATE_BUTTON,
				'REGISTRATION_DETAILS'
			)}
		>
			<FormProvider {...registrationDetailsForm}>
				<form
					onSubmit={registrationDetailsForm.handleSubmit(onFormSubmit)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={2}>
						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.legal_person_form.name_label')}
							data={selectedEntityData?.registrationDetails?.name || LONG_DASH}
							formInput={
								<TextInput
									name={REGISTRATION_DETAILS_FORM_FIELDS.registered_name}
									control={registrationDetailsForm.control}
									size="md"
									disabled={!hasEditorPermission}
								/>
							}
						/>
						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.legal_person_form.jurisdiction_label')}
							data={
								selectedEntityData?.registrationDetails?.jurisdiction
									?.jurisdiction_name || LONG_DASH
							}
							formInput={
								<Controller
									name={REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_name}
									control={registrationDetailsForm.control}
									render={({ field, fieldState }) => (
										<MantineSelect
											ref={field.ref}
											onBlur={field.onBlur}
											value={
												field.value
													? jurisdictionValueByName[field.value] ?? null
													: null
											}
											onChange={(value) => {
												field.onChange(
													value
														? jurisdictionNameByValue[value] ?? ''
														: ''
												);
												resetJurisdictionFields();
											}}
											error={fieldState.error?.message}
											renderOption={JurisdictionOption}
											placeholder={tCapManage(
												'basic_details.legal_person_form.jurisdiction_placeholder'
											)}
											size="md"
											rightSection={<Icon variant="caret-down" size="lg" />}
											rightSectionProps={{
												style: { pointerEvents: 'none' },
											}}
											data={jurisdictionsOptions}
											searchable
											clearable
											disabled={!hasEditorPermission}
											data-testid={
												TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_JURISDICTION_SELECT
											}
										/>
									)}
								/>
							}
						/>

						{showJurisdictionLanguageSelect && jurisdictionLanguages && (
							<EditableInfoField
								editMode={editMode}
								label={tCapManage(
									'basic_details.legal_person_form.jurisdiction_language_label'
								)}
								data={
									selectedEntityData?.registrationDetails
										?.selectedJurisdictionLanguage || LONG_DASH
								}
								formInput={
									<Select
										name={
											REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_language
										}
										control={registrationDetailsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										placeholder={tCapManage(
											'basic_details.legal_person_form.jurisdiction_language_placeholder'
										)}
										data={jurisdictionLanguages?.map((lang) => ({
											label: lang,
											value: lang,
										}))}
										onChange={resetLegalForm}
										searchable
										clearable
										disabled={!hasEditorPermission}
										data-testid={
											TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_JURISDICTION_LANGUAGE_SELECT
										}
										renderOption={JurisdictionLanguageOption}
									/>
								}
							/>
						)}

						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.legal_person_form.legal_form_label')}
							data={
								selectedEntityData?.registrationDetails?.legalForm
									?.entity_legal_form_name_local_name || LONG_DASH
							}
							formInput={
								<Select
									name={REGISTRATION_DETAILS_FORM_FIELDS.legal_form_id}
									control={registrationDetailsForm.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									placeholder={tCapManage(
										'basic_details.legal_person_form.legal_form_placeholder'
									)}
									data={legalFormOptions}
									searchable
									disabled={!hasEditorPermission || !legalFormOptions.length}
									data-testid={
										TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_LEGAL_FORM_SELECT
									}
									renderOption={LegalFormOption}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage(
								'basic_details.legal_person_form.registration_number_label'
							)}
							data={
								selectedEntityData?.registrationDetails
									?.corporateRegistrationNumber || LONG_DASH
							}
							formInput={
								<TextInput
									name={REGISTRATION_DETAILS_FORM_FIELDS.registration_number}
									control={registrationDetailsForm.control}
									size="md"
									placeholder={tCapManage(
										'basic_details.legal_person_form.registration_number_placeholder'
									)}
									disabled={!hasEditorPermission}
									data-testid={
										TEST_ELEMENT_IDS.CAP_REGISTRATION_DETAILS_NUMBER_INPUT
									}
								/>
							}
						/>
					</EditableInfoContainerGrid>

					{!editMode && <BasicDetailsHistoryLink />}
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
